import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, FormGroup, Label, Input } from "reactstrap";
import { CheckCircle } from "react-feather";
import {
  bulkAddMatches as bulkAddMatchesAction,
  bulkAddNextToBrands as bulkAddNextToBrandsAction,
  getBrandById as getBrandByIdAction,
  getRetailerById as getRetailerByIdAction,
} from "../ReduxToolkit/Reducers/brands";

const GRADES = ["A+", "A", "B", "C", "D"];
const PRIORITIES = ["P1", "P2", "P3"];
const MATCH_RATES = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
const LOOKUP_DEBOUNCE_MS = 500;

// Adds the table's selected rows to one target profile's matches (brands <-> retailers) or next to
// brands (brands only). The target's name shows once its ID is found, so a mistyped ID is caught
// before anything is written.
const BulkAddToProfileModal = ({ type, action, selectedIds, onClose, bulkAddMatches, bulkAddNextToBrands, getBrandById, getRetailerById }) => {
  const [targetId, setTargetId] = useState("");
  const [target, setTarget] = useState(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [grade, setGrade] = useState("A");
  const [matchRate, setMatchRate] = useState(50);
  const [priority, setPriority] = useState("P2");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isNextTo = action === "next_to";
  const targetType = isNextTo || type === "retailer" ? "brand" : "retailer";
  const listName = isNextTo ? "next to brands" : "matches";
  const count = (n) => `${n} ${type}${n === 1 ? "" : "s"}`;

  useEffect(() => {
    setTarget(null);
    setLookupError("");
    setIsLookingUp(false);
    const id = Number(targetId);
    if (!Number.isInteger(id) || id <= 0) return undefined;

    let isStale = false;
    setIsLookingUp(true);
    const timeout = setTimeout(async () => {
      const getById = targetType === "brand" ? getBrandById : getRetailerById;
      const response = await getById(id);
      if (isStale) return;
      setIsLookingUp(false);
      if (response?.data) setTarget(response.data);
      else setLookupError(`No ${targetType} with this ID`);
    }, LOOKUP_DEBOUNCE_MS);
    return () => {
      isStale = true;
      clearTimeout(timeout);
    };
  }, [targetId]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const fields = { grade, match_rate: matchRate };
    let response;
    if (isNextTo) {
      response = await bulkAddNextToBrands({ brand_id: target.id, next_to_ids: selectedIds, ...fields });
    } else {
      const sides = type === "brand"
        ? { brand_ids: selectedIds, retailer_ids: [target.id] }
        : { brand_ids: [target.id], retailer_ids: selectedIds };
      response = await bulkAddMatches({ ...sides, ...fields, priority });
    }
    setIsSubmitting(false);
    if (response?.statusCode !== 200) return; // the thunk already showed the error

    const { added, skipped } = response.data;
    if (added) {
      toast.success(`${count(added)} added to ${target.name}'s ${listName}.${skipped ? ` ${skipped} skipped (already linked).` : ""}`);
    } else {
      toast.warning(`Nothing added: ${skipped === 1 ? "this one is" : `all ${skipped} are`} already linked to ${target.name}.`);
    }
    onClose();
  };

  return (
    <Modal isOpen toggle={isSubmitting ? undefined : onClose} centered style={{ maxWidth: "520px" }}>
      <ModalHeader toggle={isSubmitting ? undefined : onClose}>
        <span style={{ fontSize: "18px", fontWeight: "700", textTransform: "uppercase" }}>
          Add to {targetType}'s {listName}
        </span>
      </ModalHeader>
      <ModalBody>
        <p className="mb-3" style={{ fontSize: "14px" }}>
          {count(selectedIds.length)} selected. Enter the ID of the {targetType} to add {selectedIds.length === 1 ? "it" : "them"} to.
        </p>

        <FormGroup>
          <Label for="bulk-add-target-id" style={{ textTransform: "capitalize" }}>{targetType} ID *</Label>
          <Input
            id="bulk-add-target-id"
            type="number"
            min="1"
            autoFocus
            value={targetId}
            invalid={!!lookupError}
            onChange={(e) => setTargetId(e.target.value)}
          />
          <div aria-live="polite" className="mt-2" style={{ fontSize: "13px", minHeight: "20px" }}>
            {isLookingUp && <Spinner size="sm" color="secondary" />}
            {lookupError && <span style={{ color: "#DC3545" }}>{lookupError}</span>}
            {target && (
              <span className="d-flex align-items-center gap-2" style={{ color: "#16803C", minWidth: 0 }}>
                <CheckCircle size={16} className="flex-shrink-0" />
                <span className="text-truncate" style={{ fontWeight: 600 }}>{target.name}</span>
                {target.website && <span className="text-truncate" style={{ color: "#6B7280" }}>{target.website}</span>}
              </span>
            )}
          </div>
        </FormGroup>

        <div className="d-flex gap-3">
          <FormGroup className="flex-fill mb-0">
            <Label for="bulk-add-grade">Grade</Label>
            <Input id="bulk-add-grade" type="select" value={grade} onChange={(e) => setGrade(e.target.value)}>
              {GRADES.map((value) => <option key={value} value={value}>{value}</option>)}
            </Input>
          </FormGroup>
          <FormGroup className="flex-fill mb-0">
            <Label for="bulk-add-match-rate">Match Rate</Label>
            <Input id="bulk-add-match-rate" type="select" value={matchRate} onChange={(e) => setMatchRate(Number(e.target.value))}>
              {MATCH_RATES.map((value) => <option key={value} value={value}>{value}%</option>)}
            </Input>
          </FormGroup>
          {!isNextTo && (
            <FormGroup className="flex-fill mb-0">
              <Label for="bulk-add-priority">Priority</Label>
              <Input id="bulk-add-priority" type="select" value={priority} onChange={(e) => setPriority(e.target.value)}>
                {PRIORITIES.map((value) => <option key={value} value={value}>{value}</option>)}
              </Input>
            </FormGroup>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" outline onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={!target || isSubmitting}>
          {isSubmitting ? <Spinner size="sm" color="light" /> : `Add ${count(selectedIds.length)}`}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const mapDispatchToProps = {
  bulkAddMatches: bulkAddMatchesAction,
  bulkAddNextToBrands: bulkAddNextToBrandsAction,
  getBrandById: getBrandByIdAction,
  getRetailerById: getRetailerByIdAction,
};

export default connect(null, mapDispatchToProps)(BulkAddToProfileModal);
